import { useEffect, useState } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { IconNavigationCross } from "@/assets/icons/figma/IconNavigationCross";
import { isUsLaunchEnabled } from "@/common/utils/us-launch.utils";
import {
  US_MARKET_GUESS_API_PATH,
  dismissUsMarketBanner,
  isUsMarketBannerDismissed,
  readSessionGuess,
  rememberSessionGuess,
  usMarketHref,
} from "@/features/us-market/us-market-banner.utils";
import { isUsLocale } from "@/i18n/config";

/**
 * WHO-6510. "Shopping from the US?" on every language but `/en-us`, for a visitor Vercel places in
 * the US. It suggests `/en-us` (lists of what ships to the US) and never redirects: the IP is only
 * a guess, and a European on a US VPN, or an American who wants the Dutch site, stays where they
 * are. Asked after mount, so the server render and the cached page are the same for everyone.
 * Closing it is remembered in this browser.
 *
 * Renders nothing while the US launch flag is off, and asks nothing either.
 */
export const UsMarketBanner = () => {
  const { locale } = useRouter();

  if (!isUsLaunchEnabled() || isUsLocale(locale)) {
    return null;
  }

  return <UsMarketBannerContent />;
};

const useFromUsGuess = () => {
  const [fromUs, setFromUs] = useState(false);

  useEffect(() => {
    if (isUsMarketBannerDismissed()) {
      return;
    }

    const remembered = readSessionGuess();

    if (remembered !== null) {
      setFromUs(remembered);
      return;
    }

    let cancelled = false;

    fetch(US_MARKET_GUESS_API_PATH)
      .then(response => (response.ok ? response.json() : null))
      .then((body: { fromUs?: boolean } | null) => {
        const guess = body?.fromUs === true;

        rememberSessionGuess(guess);

        if (!cancelled) {
          setFromUs(guess);
        }
      })
      .catch(() => {
        /* No guess, no banner. */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return [fromUs, setFromUs] as const;
};

const UsMarketBannerContent = () => {
  const { t } = useTranslation();
  const { locale, asPath } = useRouter();
  const [fromUs, setFromUs] = useFromUsGuess();

  if (!fromUs) {
    return null;
  }

  return (
    <div
      className="flex items-center gap-3 bg-brand-100 px-4 py-2.5 text-brand-700"
      data-testid="us-market-banner"
    >
      <p className="min-w-0 flex-1 font-body text-sm">
        <span className="font-bold">
          {t("common:us-market-banner-title", {
            defaultValue: "Shopping from the US?",
          })}
        </span>{" "}
        {/* A full load: the US site picks its own currency and lists on arrival. */}
        <a
          href={usMarketHref(locale, asPath)}
          hrefLang="en-US"
          className="font-semibold text-brand-500 underline"
        >
          {t("common:us-market-banner-link", {
            defaultValue: "See the pieces that ship to the US",
          })}
        </a>
      </p>
      <button
        type="button"
        onClick={() => {
          dismissUsMarketBanner();
          setFromUs(false);
        }}
        aria-label={t("common:common-close", { defaultValue: "Close" })}
        className="flex size-11 shrink-0 items-center justify-center md:size-8"
      >
        <IconNavigationCross className="size-5" />
      </button>
    </div>
  );
};
